import {
  AlignLeft,
  Bold,
  Italic,
  Underline,
  List,
  Quote,
  Type,
  CheckCircle2,
  ShieldCheck,
  MessageSquareText,
  BookMarked,
  Sparkles,
} from "lucide-react";

const ToolbarBtn = ({ icon: Icon, active = false, label }) => (
  <button
    aria-label={label}
    className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${
      active
        ? "bg-paper-2 text-forest"
        : "text-ink-soft hover:bg-paper-2 hover:text-ink"
    }`}
  >
    <Icon size={14} />
  </button>
);

export default function EditorMockup() {
  return (
    <div
      data-testid="editor-mockup"
      className="relative"
    >
      {/* Floating tag */}
      <div className="absolute -top-4 left-6 z-10 flex items-center gap-1.5 rounded-full bg-forest text-paper px-3 py-1.5 text-[11px] font-medium tracking-wide shadow-md">
        <BookMarked size={12} />
        Editor unaTESIS · APA 7 — UNAM
      </div>

      <div
        className="rounded-2xl border border-rule bg-white shadow-[0_30px_70px_-30px_rgba(39,59,44,0.25)] overflow-hidden flex flex-col"
        style={{ minHeight: 520 }}
      >
        {/* Window chrome */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-rule bg-paper-2/60">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#E2A2A2]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#E0C58A]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#A9C5A5]" />
          </div>
          <p className="font-serif text-[13px] italic text-ink-soft">
            tesis_capitulo_2.tesis
          </p>
          <span className="text-[10px] tracking-[0.18em] uppercase text-copper font-medium">
            Guardado
          </span>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-rule bg-white">
          <div className="flex items-center gap-0.5 pr-2 border-r border-rule">
            <ToolbarBtn icon={Type} label="Estilo" />
            <span className="text-[11px] text-ink-soft px-1.5">
              Cormorant · 12pt
            </span>
          </div>
          <div className="flex items-center gap-0.5 px-2 border-r border-rule">
            <ToolbarBtn icon={Bold} active label="Negrita" />
            <ToolbarBtn icon={Italic} label="Cursiva" />
            <ToolbarBtn icon={Underline} label="Subrayado" />
          </div>
          <div className="flex items-center gap-0.5 px-2 border-r border-rule">
            <ToolbarBtn icon={AlignLeft} active label="Alineación" />
            <ToolbarBtn icon={List} label="Lista" />
            <ToolbarBtn icon={Quote} label="Cita" />
          </div>
          <div className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-paper-2 px-2.5 py-1 text-[10px] font-medium text-forest">
            <Sparkles size={11} className="text-copper" />
            IA Validador activo
          </div>
        </div>

        {/* Body split */}
        <div className="grid grid-cols-12 flex-1">
          {/* Document */}
          <div className="col-span-12 md:col-span-8 bg-white px-6 sm:px-10 py-8 border-r border-rule">
            <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
              Capítulo II
            </p>
            <h3 className="font-serif text-2xl text-ink mt-2 tracking-tight">
              Marco Teórico
            </h3>
            <div className="mt-5 space-y-3.5 font-serif text-[14px] leading-[1.75] text-ink/90">
              <p>
                La adopción de tecnologías educativas en universidades públicas
                mexicanas ha mostrado un crecimiento sostenido durante la última
                década, particularmente en posgrados de ciencias sociales{" "}
                <span className="bg-[#EEE8DC] border-b border-copper/60 px-0.5 rounded-sm">
                  (Hernández&nbsp;&amp;&nbsp;Ortiz, 2022)
                </span>
                . Este fenómeno se relaciona con la migración curricular hacia
                modelos híbridos.
              </p>
              <p>
                Diversos autores coinciden en que la calidad metodológica
                depende de la trazabilidad documental{" "}
                <span className="bg-[#EEE8DC] border-b border-copper/60 px-0.5 rounded-sm">
                  (Ramírez et al., 2023)
                </span>
                , especialmente cuando se incorporan fuentes provenientes de
                repositorios institucionales como{" "}
                <span className="italic">SciELO</span> y{" "}
                <span className="italic">Redalyc</span>.<span className="inline-block w-px h-4 bg-forest align-[-2px] ml-0.5 animate-cursor-blink" />
              </p>
              <p className="text-ink-soft/80">
                En este sentido, la presente investigación retoma la propuesta
                de Cabrera (2021) como base epistemológica…
              </p>
            </div>

            {/* Page footer */}
            <div className="mt-10 pt-4 border-t border-rule flex items-center justify-between text-[11px] text-ink-soft">
              <span>Pág. 24 de 112</span>
              <span className="font-serif italic">
                UNAM · Facultad de Filosofía y Letras
              </span>
            </div>
          </div>

          {/* Sidebar AI */}
          <div className="col-span-12 md:col-span-4 bg-paper-2/40 px-5 py-6 flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <span className="h-7 w-7 grid place-items-center rounded-full bg-forest text-paper">
                <ShieldCheck size={14} />
              </span>
              <div>
                <p className="font-serif text-[15px] text-ink leading-none">
                  Sinodal IA
                </p>
                <p className="text-[10px] text-ink-soft mt-0.5">
                  Validando este párrafo…
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-rule bg-white p-3">
              <div className="flex items-start gap-2">
                <CheckCircle2
                  size={14}
                  className="text-forest mt-0.5 shrink-0"
                />
                <div>
                  <p className="text-[12px] font-medium text-ink">
                    Cita verificada
                  </p>
                  <p className="text-[11px] text-ink-soft mt-0.5 leading-snug">
                    Hernández &amp; Ortiz (2022)
                  </p>
                  <p className="text-[10px] font-mono text-copper mt-1 break-all">
                    DOI: 10.22201/iisue.2022.es.83.4
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-rule bg-white p-3">
              <div className="flex items-start gap-2">
                <CheckCircle2
                  size={14}
                  className="text-forest mt-0.5 shrink-0"
                />
                <div>
                  <p className="text-[12px] font-medium text-ink">
                    Tono académico correcto
                  </p>
                  <p className="text-[11px] text-ink-soft mt-0.5 leading-snug">
                    Voz activa · 0 muletillas detectadas
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-copper/30 bg-[#FBF6EC] p-3">
              <div className="flex items-start gap-2">
                <MessageSquareText
                  size={14}
                  className="text-copper mt-0.5 shrink-0"
                />
                <div>
                  <p className="text-[12px] font-medium text-ink">
                    Sugerencia del jurado
                  </p>
                  <p className="text-[11px] text-ink-soft mt-1 leading-snug">
                    “¿Cómo justificas la elección de SciELO frente a Scopus en
                    el contexto mexicano?”
                  </p>
                  <button className="mt-2 text-[11px] font-medium text-forest hover:text-forest-dark transition-colors">
                    Preparar respuesta →
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-auto pt-3 border-t border-rule">
              <p className="text-[10px] tracking-[0.18em] uppercase text-copper font-medium">
                Cumplimiento manual
              </p>
              <div className="mt-2 flex items-center justify-between text-[11px]">
                <span className="text-ink-soft">APA 7 · Márgenes · Citas</span>
                <span className="text-forest font-medium">98%</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-rule overflow-hidden">
                <div className="h-full bg-forest" style={{ width: "98%" }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating proof card */}
      <div className="hidden sm:flex absolute -bottom-6 -left-4 lg:-left-8 items-center gap-3 rounded-xl bg-white border border-rule shadow-xl px-4 py-3">
        <span className="grid place-items-center h-9 w-9 rounded-full bg-forest text-paper">
          <CheckCircle2 size={16} />
        </span>
        <div>
          <p className="text-[12px] font-medium text-ink leading-none">
            +1,240 tesis aprobadas
          </p>
          <p className="text-[11px] text-ink-soft mt-1">
            con 0 citas inventadas
          </p>
        </div>
      </div>
    </div>
  );
}
